import { useEffect, useState } from 'react';

type Resolved = 'light' | 'dark';

function read(): Resolved {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.dataset.theme;
  if (attr === 'dark') return 'dark';
  if (attr === 'light') return 'light';
  if (attr === 'system' || !attr) {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'light';
}

/**
 * Resolved (light | dark) theme. Re-reads when the data-theme attribute
 * on <html> changes or when system preference flips. Useful for
 * libraries that need explicit color values rather than CSS variables.
 */
export function useResolvedTheme(): Resolved {
  const [theme, setTheme] = useState<Resolved>(() => read());

  useEffect(() => {
    const update = () => setTheme(read());
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    mql?.addEventListener?.('change', update);
    return () => {
      mo.disconnect();
      mql?.removeEventListener?.('change', update);
    };
  }, []);

  return theme;
}
